import { SwitchCooldown } from '../audio/devicePolicy';
import { takePermissionStream } from '../audio/permissionStream';
import { PlatformQuirks } from '../audio/platformQuirks';
import { LoggerBridge, MIC_ACQUIRE_RETRY_DELAY_MS, PromiseMutex, VOICE_CAPTURE_CONSTRAINTS, delay, isHeadsetTrack } from '../audio/shared';

export type MicErrorCode = 'mic-permission-denied' | 'mic-unavailable' | 'mic-busy';

export class MicAcquireError extends Error {
    constructor(readonly code: MicErrorCode, message: string) {
        super(message);
        this.name = 'MicAcquireError';
    }
}

// Maps a getUserMedia failure to what the user can act on.
export function toMicErrorCode(error: unknown): MicErrorCode {
    const name = (error as any)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'mic-permission-denied';
    if (name === 'NotReadableError' || name === 'AbortError') return 'mic-busy';
    return 'mic-unavailable';
}

// One call's share of the microphone: its own clone of the capture track, which it can mute without
// affecting other calls. When the capture moves to another device, the lease adopts a clone of the
// new track and hands it to the call, which swaps it in with RTCRtpSender.replaceTrack (no renegotiation).
export class MicLease {
    private replaceHandler: ((track: MediaStreamTrack) => Promise<void>) | null = null;
    private muted = false;
    private released = false;

    constructor(private readonly source: MicSource, private current: MediaStreamTrack) { }

    get track(): MediaStreamTrack {
        return this.current;
    }

    get isMuted(): boolean {
        return this.muted;
    }

    onReplace(handler: (track: MediaStreamTrack) => Promise<void>): void {
        this.replaceHandler = handler;
    }

    // A muted track sends silence; the peer connection and the other calls' clones are untouched.
    setMuted(muted: boolean): void {
        this.muted = muted;
        this.current.enabled = !muted;
    }

    // Called by the source after the capture moved to another device.
    async adopt(track: MediaStreamTrack): Promise<void> {
        const previous = this.current;
        this.current = track;
        track.enabled = !this.muted;
        try {
            await this.replaceHandler?.(track);
        } finally {
            if (previous !== track) previous.stop();
        }
    }

    async release(): Promise<void> {
        if (this.released) return;
        this.released = true;
        this.current.stop();
        await this.source.release(this);
    }
}

// The page's single microphone capture, shared by every call. Each call leases a clone of the capture
// track, so the editor and simulator agents can run side by side. Opening a second capture instead would
// end the first one on iOS. Keeps the legacy engine's order: release the old device, platform quirks
// before getUserMedia, and the iOS route lock as the first await after it.
export class MicSource {
    private stream: MediaStream | null = null;
    private micId: string | null = null;
    private readonly mutex = new PromiseMutex();
    private readonly leases = new Set<MicLease>();
    private generation = 0;
    private trackEndedCallback: (() => void) | null = null;

    constructor(
        private readonly logger: LoggerBridge,
        private readonly quirks: PlatformQuirks,
        private readonly cooldown: SwitchCooldown) { }

    get isActive(): boolean {
        return this.stream !== null;
    }

    get currentMicId(): string | null {
        return this.micId;
    }

    get leaseCount(): number {
        return this.leases.size;
    }

    // Fired when the capture track ends outside our own teardown (permission revocation, some
    // device removals end the track without a reliable devicechange event).
    setTrackEndedCallback(callback: (() => void) | null): void {
        this.trackEndedCallback = callback;
    }

    // Opens the capture on first use and returns a clone of its track for one call. A capture that is
    // already open is shared as is, even if another device was asked for: switching it would move the
    // other calls too.
    async lease(micId: string | null): Promise<MicLease> {
        await this.mutex.lock();
        try {
            if (!this.stream || this.captureTrackEnded()) {
                await this.openLocked(micId, { explicitSwitch: false, attempts: 1 }, takePermissionStream(micId));
            } else if (micId && micId !== this.micId) {
                await this.logger.info('Microphone already open on another device; sharing it', { requestedMicId: micId, micId: this.micId });
            }
            const lease = new MicLease(this, this.captureTrack().clone());
            this.leases.add(lease);
            return lease;
        } finally {
            this.mutex.unlock();
        }
    }

    // Moves the capture to another device. Every lease adopts a clone of the new track.
    async switchTo(micId: string | null): Promise<void> {
        const gen = ++this.generation;
        await this.mutex.lock();
        try {
            if (gen !== this.generation) {
                await this.logger.info('Mic switch superseded before start; skipping', { micId });
                return;
            }
            // Nothing is capturing: the next call opens the capture on the device it asks for.
            if (this.leases.size === 0) return;
            // Cooldown starts before the async teardown so devicechange bursts fired during
            // cleanup already fall inside the suppression window.
            this.cooldown.note();
            await this.openLocked(micId, { explicitSwitch: true, attempts: 3 }, null);
            for (const lease of this.leases) {
                await lease.adopt(this.captureTrack().clone());
            }
        } finally {
            this.mutex.unlock();
        }
    }

    async release(lease: MicLease): Promise<void> {
        await this.mutex.lock();
        try {
            this.leases.delete(lease);
            if (this.leases.size === 0 && this.stream) {
                this.closeCaptureLocked();
                await this.quirks.afterMicRelease();
                await this.logger.info('Microphone released after the last call ended');
            }
        } finally {
            this.mutex.unlock();
        }
    }

    private captureTrack(): MediaStreamTrack {
        const track = this.stream?.getAudioTracks()[0];
        if (!track) throw new MicAcquireError('mic-unavailable', 'The microphone capture has no audio track');
        return track;
    }

    private captureTrackEnded(): boolean {
        return this.stream?.getAudioTracks()[0]?.readyState !== 'live';
    }

    private async openLocked(micId: string | null, opts: { explicitSwitch: boolean; attempts: number }, initialStream: MediaStream | null): Promise<void> {
        // Release the previous device fully first, including the calls' clones: on mobile the next
        // getUserMedia fails or picks the wrong route while the old device is still held.
        this.closeCaptureLocked();
        for (const lease of this.leases) lease.track.stop();
        await this.quirks.beforeMicAcquire(opts.explicitSwitch);

        const stream = initialStream ?? await this.acquire(micId, opts.attempts);
        const track = stream.getAudioTracks()[0] || null;
        // Time-critical on iOS: must be the first await after getUserMedia resolves.
        await this.quirks.afterMicAcquire(!!track && isHeadsetTrack(track));
        if (!track) {
            stream.getTracks().forEach(t => t.stop());
            throw new MicAcquireError('mic-unavailable', 'The microphone capture has no audio track');
        }

        track.onended = () => {
            if (this.stream !== stream) return; // ended by our own teardown
            void this.logger.warning('Mic track ended unexpectedly', { micId, label: track.label });
            this.trackEndedCallback?.();
        };
        this.stream = stream;
        this.micId = micId;

        await this.logger.info('Selected microphone device', {
            requestedId: micId,
            actualTrackDeviceId: track.getSettings?.()?.deviceId ?? null,
            trackLabel: track.label,
            explicitSwitch: opts.explicitSwitch
        });
        await this.quirks.logSwitchDiagnostics('afterAcquire', {
            requestedMicId: micId,
            streamIsHeadset: isHeadsetTrack(track),
            actualTrackDeviceId: track.getSettings?.()?.deviceId ?? null,
            actualTrackLabel: track.label
        });
    }

    private closeCaptureLocked(): void {
        const stream = this.stream;
        this.stream = null;
        this.micId = null;
        stream?.getTracks().forEach(track => {
            track.onended = null; // our own stop must not fire the ended callback
            track.stop();
        });
    }

    private async acquire(micId: string | null, maxAttempts: number): Promise<MediaStream> {
        const audio: MediaTrackConstraints = { ...VOICE_CAPTURE_CONSTRAINTS };
        if (micId) audio.deviceId = { exact: micId };
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await navigator.mediaDevices.getUserMedia({ video: false, audio });
            } catch (err) {
                lastError = err;
                await this.logger.warning(`Mic acquisition attempt ${attempt} failed`, {
                    attempt, deviceId: micId, error: String(err)
                });
                // A refused permission doesn't change on retry.
                if (toMicErrorCode(err) === 'mic-permission-denied') break;
                if (attempt < maxAttempts) await delay(MIC_ACQUIRE_RETRY_DELAY_MS);
            }
        }
        const code = toMicErrorCode(lastError);
        await this.logger.error('Failed to access mic', String(lastError), { deviceId: micId, maxAttempts, code });
        throw new MicAcquireError(code, String(lastError));
    }
}
